import { Button, formatPrice, Select } from '@artosera/ui';
import {
  can,
  formatDate,
  sujet,
  type ConsoleGroupe,
  type MontantParDevise,
} from '@artosera/shared';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useConsoleGroupe } from '../../api/groupe.js';
import { estApiError } from '../../api/errors.js';
import { galerieAModule, groupeDeLaGalerie } from '../../auth/sujet.js';
import { Monogramme } from '../../composants/Monogramme.js';
import { useShellContext } from '../shell/context.js';
import styles from './GroupePage.module.css';

type Periode = 'janvier' | 'trente' | 'douze';

function depuisDe(periode: Periode, maintenant = new Date()): string | null {
  if (periode === 'janvier') return null;
  const jours = periode === 'trente' ? 30 : 365;
  return new Date(maintenant.getTime() - jours * 86_400_000).toISOString().slice(0, 10);
}

/** « Groupe » (3.2, 5.2, M12) : la console consolidée, en lecture seule. */
export function GroupePage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'coffre', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const { galerieSlug, session } = useShellContext();
  const [periode, setPeriode] = useState<Periode>('janvier');
  const galerie = session.galeries.find((candidate) => candidate.slug === galerieSlug);
  const groupe =
    galerie && galerieAModule(galerie, 'console-groupe')
      ? groupeDeLaGalerie(session.compte, galerie)
      : null;
  const autorise = groupe !== null && can(sujet(groupe.role), 'group:read');
  const consoleGroupe = useConsoleGroupe(galerieSlug, depuisDe(periode), autorise ? groupe : null);
  const slugsDeLaSession = new Set(session.galeries.map((galerie) => galerie.slug));

  const montants = (liste: readonly MontantParDevise[] | null | undefined) =>
    liste && liste.length > 0
      ? liste.map((montant) => (
          <span key={montant.devise} className={styles.montant}>
            {formatPrice(montant.centimes, langue, montant.devise)}
          </span>
        ))
      : t('regie:groupe.aucunMontant');

  // Désactiver une requête ne masque pas ses données en cache : le rendu
  // reste borné par la session affichée, même pendant la purge asynchrone.
  if (!autorise) {
    return (
      <p className={styles.vide} role="alert">
        {t('regie:groupe.accesRefuse')}
      </p>
    );
  }
  if (consoleGroupe.isLoading) return <p className={styles.vide}>{t('regie:commun.chargement')}</p>;
  if (consoleGroupe.isError || !consoleGroupe.data) {
    return (
      <div className={styles.vide} role="alert">
        {estApiError(consoleGroupe.error) ? consoleGroupe.error.message : t('commun:erreurReseau')}
        <Button onClick={() => void consoleGroupe.refetch()}>{t('regie:groupe.reessayer')}</Button>
      </div>
    );
  }
  const donnees: ConsoleGroupe = consoleGroupe.data;
  if (
    donnees.groupe.id !== groupe.id ||
    donnees.groupe.slug !== groupe.slug ||
    donnees.groupe.role !== groupe.role
  ) {
    return (
      <div className={styles.vide} role="alert">
        {t('regie:groupe.donneesObsoletes')}
        <Button onClick={() => void consoleGroupe.refetch()}>{t('regie:groupe.reessayer')}</Button>
      </div>
    );
  }
  const echeances = donnees.galeries
    .flatMap((galerie) =>
      galerie.incluse
        ? (galerie.echeances ?? []).map((echeance) => ({ ...echeance, galerie: galerie.nom }))
        : [],
    )
    .sort((a, b) => a.echeanceLe.localeCompare(b.echeanceLe));
  const sommeNombres = (liste: readonly MontantParDevise[]) =>
    liste.reduce((total, montant) => total + montant.nombre, 0);

  return (
    <div className={styles.page} data-ui="group-console">
      <header className={styles.entete} data-ui="page-heading">
        <h1 className={styles.titre} data-ui="page-title">
          {donnees.groupe.nom}
        </h1>
        <p className={styles.sousTitre}>{t('regie:groupe.sousTitre')}</p>
        <Select
          aria-label={t('regie:groupe.periode')}
          value={periode}
          onValueChange={(valeur) => setPeriode(valeur as Periode)}
          options={[
            { value: 'janvier', label: t('regie:groupe.depuisJanvier') },
            { value: 'trente', label: t('regie:groupe.trenteJours') },
            { value: 'douze', label: t('regie:groupe.douzeMois') },
          ]}
        />
      </header>

      <section className={styles.tuiles} aria-label={t('regie:groupe.sousTitre')}>
        <article className={styles.tuile} data-ui="dashboard-metric">
          <h2>{t('regie:groupe.stock')}</h2>
          <p>
            {donnees.partages.oeuvres
              ? montants(donnees.totaux.stock)
              : t('regie:groupe.nonPartage')}
          </p>
          {donnees.partages.oeuvres ? (
            <small>
              {t('regie:groupe.stockDetail', { nombre: sommeNombres(donnees.totaux.stock) })}
            </small>
          ) : null}
        </article>
        <article className={styles.tuile} data-ui="dashboard-metric">
          <h2>{t('regie:groupe.factures')}</h2>
          <p>
            {donnees.partages.ventes
              ? montants(donnees.totaux.factures)
              : t('regie:groupe.nonPartage')}
          </p>
          {donnees.partages.ventes ? (
            <small>
              {t('regie:groupe.facturesDetail', { nombre: sommeNombres(donnees.totaux.factures) })}
            </small>
          ) : null}
        </article>
        <article className={styles.tuile} data-ui="dashboard-metric">
          <h2>{t('regie:groupe.ventesSimples')}</h2>
          <p>
            {donnees.partages.ventes
              ? montants(donnees.totaux.ventesSimples)
              : t('regie:groupe.nonPartage')}
          </p>
          {donnees.partages.ventes ? (
            <small>
              {t('regie:groupe.ventesSimplesDetail', {
                nombre: sommeNombres(donnees.totaux.ventesSimples),
              })}
            </small>
          ) : null}
        </article>
      </section>

      {donnees.galeries.every((galerie) => !galerie.incluse) ? (
        <p className={styles.vide}>{t('regie:groupe.aucuneGalerie')}</p>
      ) : null}
      <ul className={styles.galeries}>
        {donnees.galeries.map((galerie) => (
          <li key={galerie.id} className={styles.galerie} data-incluse={galerie.incluse}>
            <Monogramme nom={galerie.nom} taille={48} forme="carre" />
            <span className={styles.nom}>{galerie.nom}</span>
            {galerie.incluse ? (
              <div className={styles.detail}>
                {galerie.chiffres ? (
                  <span>{t('regie:groupe.chiffres', galerie.chiffres)}</span>
                ) : null}
                <dl className={styles.mesures}>
                  <div>
                    <dt>{t('regie:groupe.stock')}</dt>
                    <dd>
                      {galerie.stock === null
                        ? t('regie:groupe.nonPartage')
                        : montants(galerie.stock)}
                    </dd>
                  </div>
                  {galerie.ventes ? (
                    <>
                      <div>
                        <dt>{t('regie:groupe.factures')}</dt>
                        <dd>{montants(galerie.ventes.factures)}</dd>
                      </div>
                      <div>
                        <dt>{t('regie:groupe.ventesSimples')}</dt>
                        <dd>{montants(galerie.ventes.ventesSimples)}</dd>
                      </div>
                    </>
                  ) : null}
                </dl>
              </div>
            ) : (
              <span className={styles.exclue}>
                {galerie.raison === 'module_inactif'
                  ? t('regie:groupe.moduleInactif')
                  : t('regie:groupe.indisponible')}
              </span>
            )}
            {slugsDeLaSession.has(galerie.slug) ? (
              <Link to={`/${galerie.slug}`}>{t('regie:groupe.ouvrir')}</Link>
            ) : null}
          </li>
        ))}
      </ul>

      <section>
        <table className={styles.tableau} aria-label={t('regie:groupe.echeances')}>
          <caption>{t('regie:groupe.echeances')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('regie:groupe.colonneDate')}</th>
              <th scope="col">{t('regie:groupe.colonneGalerie')}</th>
              <th scope="col">{t('regie:groupe.colonneType')}</th>
              <th scope="col">{t('regie:groupe.colonneTitre')}</th>
            </tr>
          </thead>
          <tbody>
            {echeances.length === 0 ? (
              <tr>
                <td colSpan={4}>
                  {t(
                    donnees.partages.echeancesCoffre
                      ? 'regie:groupe.aucuneEcheance'
                      : 'regie:groupe.nonPartage',
                  )}
                </td>
              </tr>
            ) : (
              echeances.map((echeance) => (
                <tr key={`${echeance.galerie}-${echeance.id}`}>
                  <td data-label={t('regie:groupe.colonneDate')}>
                    {formatDate(new Date(echeance.echeanceLe), langue, 'court')}
                  </td>
                  <td data-label={t('regie:groupe.colonneGalerie')}>{echeance.galerie}</td>
                  <td data-label={t('regie:groupe.colonneType')}>
                    {t(`coffre:types.${echeance.type}`, { defaultValue: echeance.type })}
                  </td>
                  <td data-label={t('regie:groupe.colonneTitre')}>
                    {echeance.confidentiel ? t('regie:groupe.confidentiel') : echeance.titre}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <p className={styles.mention}>{t('regie:groupe.aucunFichier')}</p>
      </section>
    </div>
  );
}
